import React, { ReactElement, ReactNode, useContext, useMemo, useState } from 'react'
// Utilities imports.
import { useDroppable } from '@dnd-kit/core'
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
// Commons imports.
import Icon, { IconName } from '../../Common/Components/Icon'
// Contexts imports.
import { MessageContext, PromptContext, ServletPathContext, UserInfoContext } from '../../Common/Contexts'
// Utilities imports.
// Sub imports.
import { LanguageMyReportList, LanguagePrompt } from '../../../../i18n'
import { arrayFromMapIterator } from '../../../NonReact/utils/Utils'
import ContextMenu, { ContextMenuPosition } from '../../Common/Components/ContextMenu'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { toReportListItemsAsTree } from '../../Common/Utils/Reports'
import BinnedReportListItem, { BinnedReportListItemPropagatedValues } from './BinnedReportListItem'
import { ReportListItemsIndexByReportID, ReportListItemType } from './Types'

import { ReportingAccessLevels } from '../../AddUser/Reporting/ReportingAccessLevels'
import { StateSetter } from '../../Common/Types/Types'
import { PrepareDeletePermanentlyResultMessage, PrepareRestoreResultMessage } from '../../Common/Utils/ReportList'
import { deleteReportFromBin, restoreReportFromBin } from './API'
import { ReportDragDropClasses, ReportDragDropIDs } from './Constants'
import PromptMessageWithWarning from './PromptMessageWithWarning'

type Props = {
    reportListItemsAsMap: ReportListItemsIndexByReportID
    setReportListItemsAsMap: StateSetter<ReportListItemsIndexByReportID>
    hoveredItemID: Nullable<string>
    setHoveredItemID: StateSetter<Nullable<string>>
    checkedReportBinItemsIDs: string[]
    setCheckedReportBinItemsIDs: StateSetter<string[]>
    initialiseReportListData: () => Promise<void>
    setHasHighlight: (value: boolean) => void
    hasHighlight: boolean
    highlightRows: boolean
    handleKeyDownRoot: (event: React.KeyboardEvent<HTMLDivElement>) => void
}

/**
 * Reports list recycle bin component.
 *
 * @param reportListItemsAsMap Report items as map indexed by ID.
 * @param setReportListItemsAsMap The setter for corresponding report items map.
 * @param hoveredItemID The ID of currently hovered item.
 * @param setHoveredItemID Setter for corresponding property.
 * @param editingItemID The ID of currently editing item - we support only one at the same time.
 * @param checkedReportBinItemsIDs Array of checked items IDs within recycle bin.
 * @param setCheckedReportBinItemsIDs Setter for corresponding property.
 * @param initialiseReportListData Report list data initialisation method.
 * @param setHasHighlight Setter for corresponding property.
 * @param hasHighlight Flag whether there is a highlighted row.
 * @param highlightRows Flag whether to highlight rows.
 * @param handleKeyDownRoot The key down handler for the root element.
 */
const ReportRecycleBin: React.FC<Props> = ({
    reportListItemsAsMap,
    setReportListItemsAsMap,
    hoveredItemID,
    setHoveredItemID,
    checkedReportBinItemsIDs,
    setCheckedReportBinItemsIDs,
    initialiseReportListData,
    setHasHighlight,
    hasHighlight,
    highlightRows,
    handleKeyDownRoot
}): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('LanguagePrompt')
    language.loadBundle('LanguageMyReportList')

    const name = language.get(LanguageMyReportList.ReportlistRecyclebin)
    const id = 'RECYCLE_BIN'
    const [isBinOpen, setIsBinOpen] = useState<boolean>(false)

    const servletPath = useContext(ServletPathContext) as string
    const { displayErrorMessage, displaySuccessMessage, displayWarningMessage } = useContext(MessageContext)
    const { displayPrompt } = useContext(PromptContext)
    const userInfo = useContext(UserInfoContext)
    const isEssentialsUser: boolean = useMemo(
        (): boolean => userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.ESSENTIALS,
        [userInfo]
    )
    const isSignInAs: boolean = useMemo((): boolean => userInfo?.SessionInfo?.SignInAsActive ?? false, [userInfo])
    const isSignInAsActive: boolean = useMemo((): boolean => {
        if (isEssentialsUser) {
            return false
        }

        return isSignInAs
    }, [isEssentialsUser, isSignInAs])
    const isUserReportReadOnly = userInfo?.Scope?.ReportReadOnly || false

    const reportListItemsAsArray = arrayFromMapIterator(reportListItemsAsMap)
    const binnedReportItems = reportListItemsAsArray.filter((reportListItem) => reportListItem.isBinned)
    const isBinEmpty = binnedReportItems && binnedReportItems.length === 0

    const [isHighlightWholeRecycleBinContent, setIsHighlightWholeRecycleBinContent] = useState<boolean>(false)

    const [isShowContentUntilTransitionFinishes, setIsShowContentUntilTransitionFinishes] = useState<boolean>(false)

    /**
     * Empty Recycle Bin click handler.
     */
    const handleEmptyRecycleBin = async (): Promise<void> => {
        const checkedReports: ReportListItemType[] = binnedReportItems.filter(
            (binnedReportItem) => binnedReportItem.isReport
        )
        const deletedReportIds: string[] = []
        const failedReportIDs: string[] = []

        for (let index = 0; index < checkedReports.length; index += 1) {
            try {
                // eslint-disable-next-line
                const reportDeleteResponse = await deleteReportFromBin(servletPath, checkedReports[index].id)
                // Add the ID of deleted item to the temporary array so that we can then "unselect" all successfully
                // deleted items.
                deletedReportIds.push(checkedReports[index].id)
            } catch (error) {
                failedReportIDs.push(checkedReports[index].id)
            }
        }

        if (deletedReportIds.length === 0) {
            // Error
            displayErrorMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIds.length, checkedReports.length)
            )
        } else if (failedReportIDs.length === 0) {
            // Success
            displaySuccessMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIds.length, checkedReports.length)
            )
        } else if (failedReportIDs.length > 0 && deletedReportIds.length > 0) {
            // Warning
            displayWarningMessage(
                PrepareDeletePermanentlyResultMessage(language, deletedReportIds.length, checkedReports.length)
            )
        }

        // Unselect the restored items. (Clean-up the selected items IDs list)
        setCheckedReportBinItemsIDs((prevCheckedItemsIDs) =>
            prevCheckedItemsIDs.filter((rptListItemID) => deletedReportIds.indexOf(rptListItemID) === -1)
        )

        initialiseReportListData()
    }

    /**
     * Empty Recycle Bin click handler.
     */
    const handleRestoreAllReports = async (): Promise<void> => {
        const checkedReports: ReportListItemType[] = binnedReportItems.filter(
            (binnedReportItem) => binnedReportItem.isReport
        )
        const restoredReportIDs: string[] = []
        const failedReportIDs: string[] = []
        for (let index = 0; index < checkedReports.length; index += 1) {
            try {
                // eslint-disable-next-line
                const reportDeleteResponse = await restoreReportFromBin(servletPath, checkedReports[index].id)
                // Add the ID of restored item to the temporary array so that we can then "unselect" all
                // successfully restored items.
                restoredReportIDs.push(checkedReports[index].id)
            } catch (error) {
                failedReportIDs.push(checkedReports[index].id)
            }
        }

        if (restoredReportIDs.length === 0) {
            // Error
            displayErrorMessage(PrepareRestoreResultMessage(language, restoredReportIDs.length, checkedReports.length))
        } else if (failedReportIDs.length === 0) {
            // Success
            displaySuccessMessage(
                PrepareRestoreResultMessage(language, restoredReportIDs.length, checkedReports.length)
            )
        } else if (failedReportIDs.length > 0 && restoredReportIDs.length > 0) {
            // Warning
            displayWarningMessage(
                PrepareRestoreResultMessage(language, restoredReportIDs.length, checkedReports.length)
            )
        }

        // Unselect the restored items. (Clean-up the selected items IDs list)
        setCheckedReportBinItemsIDs((prevCheckedItemsIDs) =>
            prevCheckedItemsIDs.filter((rptListItemID) => restoredReportIDs.indexOf(rptListItemID) === -1)
        )

        initialiseReportListData()
    }

    /**
     * Prompt popup warning before deletion of reports.
     */
    const displayPromptOnEmptyRecycleBin = (): void => {
        displayPrompt(
            <PromptMessageWithWarning
                message={language?.get(LanguagePrompt.PromptDeletereports)}
                warningMessage={language?.get(LanguagePrompt.PromptReportswillbedeletedpermanently)}
            />,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
            () => {
                handleEmptyRecycleBin()
            },
            undefined,
            undefined,
            undefined
        )
    }

    /**
     * Builds menu items converted to react elements.
     */
    const getDeletedReportListItems = (): ReactNode => {
        if (reportListItemsAsMap.size > 0) {
            const reportListItemsAsTree = toReportListItemsAsTree(binnedReportItems, undefined)

            const propagatedValues: BinnedReportListItemPropagatedValues = {
                isReportWriteAllowed: !isUserReportReadOnly || isSignInAsActive,
                hoveredItemID,
                setHoveredItemID,
                checkedItemsIDs: checkedReportBinItemsIDs,
                setCheckedItemsIDs: setCheckedReportBinItemsIDs,
                reportListItemsAsMap,
                setReportListItemsAsMap,
                initialiseReportListData,
                setHasHighlight
            }

            return reportListItemsAsTree.map((value) => (
                <BinnedReportListItem
                    key={value.id}
                    reportListItem={value}
                    propagatedValues={propagatedValues}
                    isParentFolderHighlighted={isHighlightWholeRecycleBinContent}
                />
            ))
        }
        return <></>
    }

    const onMouseOverReportContextMenuAction = (): void => {
        setIsHighlightWholeRecycleBinContent(true)
        setHasHighlight(true)
    }
    const onMouseOutReportContextMenuAction = (): void => {
        setIsHighlightWholeRecycleBinContent(false)
        setHasHighlight(false)
    }

    const { isOver: isOverRecyclingBin, setNodeRef: droppableRecycleBinRef } = useDroppable({
        id: ReportDragDropIDs.DROPPABLE_BIN
    })

    return (
        <div
            onKeyDown={(event): void => handleKeyDownRoot(event)}
            ref={droppableRecycleBinRef}
            className={`report-list-container ${highlightRows ? 'highlight-rows' : ''} ${
                hasHighlight ? 'has-highlight' : ''
            } ${isHighlightWholeRecycleBinContent ? 'highlight-all-rows' : ''}`}
        >
            <div className='report-list-wrapper'>
                <Accordion
                    key={`${name}-${id}`}
                    expanded={isBinOpen}
                    onChange={(): void => setIsBinOpen(!isBinOpen)}
                    disabled={isBinEmpty}
                    TransitionProps={{
                        onEnter: (): void => setIsShowContentUntilTransitionFinishes(true),
                        onExited: (): void => setIsShowContentUntilTransitionFinishes(false)
                    }}
                >
                    <AccordionSummary
                        className={`summary binSummary ${isOverRecyclingBin ? ReportDragDropClasses.DND_BIN : ``}`}
                        expandIcon={<Icon iconName={IconName.DELETE_SWEEP} />}
                        IconButtonProps={{ disableRipple: true, disableFocusRipple: true }}
                        aria-controls='panel1a-content'
                        id='panel1a-header'
                    >
                        {name}

                        {!isBinEmpty && (
                            <ContextMenu
                                menuItems={[
                                    {
                                        itemName: language.get(LanguageMyReportList.ReportlistEmptyrecyclebin),
                                        isEnabled: true, // Bin is Not Empty
                                        icon: <Icon iconName={IconName.DELETE_FOREVER} />,
                                        onClick: (event): void => {
                                            displayPromptOnEmptyRecycleBin()
                                            event.preventDefault()
                                            event.stopPropagation()
                                            onMouseOutReportContextMenuAction()
                                        },
                                        onMouseOver: onMouseOverReportContextMenuAction,
                                        onMouseOut: onMouseOutReportContextMenuAction
                                    },
                                    {
                                        itemName: language.get(LanguageMyReportList.ReportlistRestoreallreports),
                                        isEnabled: true, // Bin is Not Empty
                                        icon: <Icon iconName={IconName.RESTORE_FROM_TRASH} />,
                                        onClick: (event): void => {
                                            handleRestoreAllReports()
                                            event.preventDefault()
                                            event.stopPropagation()
                                            onMouseOutReportContextMenuAction()
                                        },
                                        onMouseOver: onMouseOverReportContextMenuAction,
                                        onMouseOut: onMouseOutReportContextMenuAction
                                    }
                                ]}
                                position={ContextMenuPosition.TOP_RIGHT}
                                anchorElement={
                                    <div className='edit-icon-wrapper'>
                                        <IconButton
                                            type={IconButtonType.BUTTON}
                                            iconName={IconName.MORE_HORIZONTAL}
                                            title={language.get(LanguageMyReportList.ReportlistMoreoptions)}
                                        />
                                    </div>
                                }
                            />
                        )}
                    </AccordionSummary>
                    {binnedReportItems && binnedReportItems.length > 0 && (
                        <AccordionDetails>
                            {(isBinOpen || isShowContentUntilTransitionFinishes) && (
                                <div className={`report-list-wrapper`}>{getDeletedReportListItems()}</div>
                            )}
                        </AccordionDetails>
                    )}
                </Accordion>
            </div>
        </div>
    )
}

export default ReportRecycleBin
